/**
 * LANE V2 — the REST client for MSA's session-keyed routes (`/api/sessions/**`).
 *
 * One function per route in the read-model spec (R0–R5) plus the write routes that already exist on
 * dev (ticket, turns, respond, turn cancel, task cancel, task revert). Every function goes through
 * `request`, so there is ONE way a lane-v2 failure is read: MSA's `coded_errors()` raises
 * `HTTPException(detail={"error_code": ...})`, which FastAPI serialises as `{detail:{error_code}}`;
 * a few routes answer a bare `{error_code}`. Both become a `LaneV2Error{status, code, message}`.
 *
 * ── `POST /turns` IS A BONUS, NOT THE TRUTH ────────────────────────────────────────────────────────
 *
 * The turn route BLOCKS for the whole turn (a build can run for most of an hour) and the proxy in
 * front of MSA cuts a request at 600 s. So its response is used when it arrives and never waited on
 * for correctness: the session stream plus the snapshot GETs (R1/R2/R3/R5) are what the view renders.
 * `submitTurn` therefore reports a gateway timeout / dropped connection as `{kind:"detached"}` — the
 * turn is very likely still running server-side, and the stream will say so — never as a failure.
 * A 503 CAPACITY is the one pre-work refusal worth retrying automatically (nothing was written), with
 * the server's `Retry-After`, bounded.
 */
import { getMultiAgentAuthHeaders, multiAgentBaseUrl } from "../client";
import type { InteractionSubject, TaskGraph } from "../protocol";
import type {
  AskResolution,
  AskView,
  AttachmentIn,
  BranchConflictBody,
  BranchSaveAccepted,
  BranchSavePoll,
  BranchSaveRejectedBody,
  IdeStateIn,
  ItemBody,
  LaneV2Capabilities,
  LaneV2Session,
  RevertOut,
  SaveBranchIn,
  SaveBranchOut,
  SessionCloseOut,
  SessionCompactOut,
  SessionCompactionOut,
  SessionListOut,
  TaskCancelOut,
  TaskHoldOut,
  TaskSnapshot,
  TicketOut,
  ValidateOut,
  RequestChangesOut,
  DiscardOut,
  TurnOut,
  TurnView,
} from "./types";

/** Resolved at call time (not module load) so a test can point it elsewhere. */
function base(): string {
  return multiAgentBaseUrl;
}

// ── errors ───────────────────────────────────────────────────────────────────────────────────────

/** Every non-2xx from a lane-v2 route. `code` is MSA's `error_code` when it sent one. */
export class LaneV2Error extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly payload: unknown;
  /** `Retry-After` in ms, when the server sent one (503 CAPACITY). */
  readonly retryAfterMs: number | null;
  constructor(status: number, code: string | null, message: string, payload: unknown, retryAfterMs: number | null = null) {
    super(message);
    this.name = "LaneV2Error";
    this.status = status;
    this.code = code;
    this.payload = payload;
    this.retryAfterMs = retryAfterMs;
  }
}

export function isLaneV2Error(err: unknown): err is LaneV2Error {
  return err instanceof LaneV2Error;
}

/** `409 BRANCH_CONFLICT` carries the peer's own `{revision, files}` verbatim — never a bare `{error_code}`. */
export function branchConflictOf(err: LaneV2Error): BranchConflictBody | null {
  if (err.code !== "BRANCH_CONFLICT" || !err.payload || typeof err.payload !== "object") return null;
  const body = err.payload as Partial<BranchConflictBody>;
  return { error_code: "BRANCH_CONFLICT", revision: body.revision ?? null,
    files: Array.isArray(body.files) ? body.files : [],
    written: Array.isArray(body.written) ? body.written : [],
  };
}

/** `422 BRANCH_SAVE_REJECTED` carries only the peer's own machine code — never its free-text body. */
export function branchSaveRejectionOf(err: LaneV2Error): BranchSaveRejectedBody | null {
  if (err.code !== "BRANCH_SAVE_REJECTED" || !err.payload || typeof err.payload !== "object") return null;
  const body = err.payload as Partial<BranchSaveRejectedBody>;
  return { error_code: "BRANCH_SAVE_REJECTED", peer_code: typeof body.peer_code === "string" ? body.peer_code : null };
}

/**
 * Bare-string `detail` tokens the proxy (not MSA) answers with, mapped to the typed code they stand for. The proxy's
 * 429 budget refusal is `{"detail":"llm_budget_exceeded"}` today and `{"detail":..., "error_code":"LLM_BUDGET_EXCEEDED"}`
 * soon; this is a fixed machine token from a spec, not prose (rule 22 role iii).
 */
const PROXY_DETAIL_CODES: Readonly<Record<string, string>> = { llm_budget_exceeded: "LLM_BUDGET_EXCEEDED" };

/**
 * MSA's error code from a parsed body: `{error_code}`, `{detail:{error_code}}`, or one of the proxy's fixed bare-string
 * `detail` tokens (`PROXY_DETAIL_CODES`). `null` otherwise (any other bare string, an HTML proxy page, nothing at all).
 */
export function errorCodeOf(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const top = body as Record<string, unknown>;
  if (typeof top.error_code === "string" && top.error_code) return top.error_code;
  const detail = top.detail;
  if (detail && typeof detail === "object") {
    const code = (detail as Record<string, unknown>).error_code;
    if (typeof code === "string" && code) return code;
  }
  if (typeof detail === "string" && Object.prototype.hasOwnProperty.call(PROXY_DETAIL_CODES, detail)) return PROXY_DETAIL_CODES[detail];
  return null;
}

/** `Retry-After` (delta-seconds or an HTTP date) → ms, or null. */
export function parseRetryAfter(value: string | null | undefined, now: number = Date.now()): number | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed) * 1000;
  const at = Date.parse(trimmed);
  return Number.isFinite(at) ? Math.max(0, at - now) : null;
}

/** `422 FREE_TEXT_REQUIRED`: a park card's "Describe a change" was sent without words. */
/** The offline line (503 LANE_V2_OFFLINE), also shown by the card when the kill switch is on. */
export const LANE_V2_OFFLINE_COPY = "This is turned off for maintenance right now. You can still decline or cancel. Try again shortly.";
export const FREE_TEXT_REQUIRED_COPY = "Describe the change you want first.";

/**
 * What to tell a person about a lane-v2 failure. Fixed copy per code — never the server's body,
 * which can carry internals (frontend rules §10). Unknown codes get a generic line with the status.
 */
export function describeLaneV2Error(err: LaneV2Error): string {
  switch (err.code) {
    case "STALE_SUBJECT":
      return "This proposal changed. Review the new one.";
    case "GRANT_STALE_HEAD":
    case "STALE_TRANSITION":
      return "The task moved on since this was shown. Review the current state.";
    case "ANSWER_NOT_APPLICABLE":
      return "That option no longer applies to this task. Pick another.";
    case "DEPLOY_NOT_ENABLED":
      // Two routes answer this: respond (a revert's apply step) and validate (`describeValidateFailure` words that one itself).
      return "That step is turned off in this environment. Nothing was changed. You can still decline or request changes.";
    case "DEPLOY_NOT_OFFERED":
      return "Deploying from here is not offered any more. You can save your change to a branch or check it against the org.";
    case "VALIDATION_IN_PROGRESS":
      return "A check against the org is already running. Wait for it to finish.";
    case "NOT_VALIDATABLE":
      return "This change cannot be checked right now. It may have moved on. Refresh and look at its current state.";
    case "REVALIDATE_LIMIT_REACHED":
      return "You have reached the limit of re-checks for this change. Ask for changes to start a new one.";
    case "SAVE_BRANCH_NOT_NAMED":
      return "Name the branch to save to, or use the Save button.";
    case "ASK_NOT_PENDING":
    case "ASK_CONFLICT":
      return "This question was already answered or withdrawn.";
    case "ASK_NOT_FOUND":
      return "This question no longer exists.";
    case "ASK_NOT_CONTINUABLE":
      return "This question can no longer be continued. Refresh and try again.";
    case "TURN_BUSY":
      return "A turn is still running in this conversation. Wait for it to finish, or stop it.";
    case "CAPACITY":
      return "The service is busy right now. Please try again in a moment.";
    case "LANE_V2_NOT_ENABLED":
      return "This conversation type is not enabled for your organization.";
    case "WRONG_LANE":
      return "This conversation belongs to the classic chat and cannot be continued here.";
    case "SESSION_NOT_FOUND":
      return "This conversation could not be found.";
    case "SESSION_CLOSED":
      return "This conversation is closed.";
    case "TASK_NOT_FOUND":
      return "This task could not be found.";
    case "TASK_CLOSED":
      return "This task is already closed.";
    case "LANE_V2_OFFLINE":
      return err.retryAfterMs && err.retryAfterMs > 0
        ? `This is turned off for maintenance right now. You can still decline or cancel. Try again in about ${Math.max(1, Math.ceil(err.retryAfterMs / 1000))} seconds.`
        : LANE_V2_OFFLINE_COPY;
    case "SESSION_BUSY":
      return "A reply is still running. Wait for it to finish, or stop it.";
    case "LLM_BUDGET_EXCEEDED":
      return "Your organization's usage budget is used up, so nothing new can be started. Your conversations and history are still readable. Ask an admin to review usage and the budget.";
    case "HOLD_BUDGET_STILL_EXHAUSTED":
      return "Still over the usage budget \u2014 try again later.";
    case "REVERT_AWAITING_APPROVAL":
      return "Approve or decline the revert first.";
    case "FREE_TEXT_REQUIRED":
      return FREE_TEXT_REQUIRED_COPY;
    case "HOLD_REQUEST_INVALID":
      return "That pause or resume does not apply to the task in its current state.";
    case "NOT_DISCARDABLE":
      return "This change can no longer be discarded. It may have moved on. Refresh and look at its current state.";
    case "NOT_CHANGEABLE":
      return "Changes can no longer be requested on this one. It may have moved on. Refresh and look at its current state.";
    case "HUMAN_CHANGE_CAP_REACHED":
      return "This change has used all of its change-request rounds. You can still save it or discard it, or start a new request.";
    case "SAVE_TARGET_IS_LOCAL":
      return "This conversation works on your local workspace, so there is no branch to save to from here.";
    case "ILLEGAL_TRANSITION":
    case "TRANSITION_REFUSED":
      return "The task cannot do that in its current state. Refresh to see where it stands.";
    case "VALIDATION_NOT_PASSED":
      return "The check against the org did not pass, so this cannot be applied. Ask for changes or start a new request.";
    case "ASK_INVALID":
      return "That answer was not accepted for this question. Pick one of the offered options.";
    case "WORKSPACE_SCOPE_MISMATCH":
      return "This conversation is tied to a different workspace. Start a new chat for this workspace.";
    case "WORKSPACE_NOT_FOUND":
      return "That workspace is not registered for your account. Reconnect it from your IDE.";
    case "WORKSPACE_INVALID":
      return "The workspace reference was not valid. This is a bug. Please report it.";
    case "WORKSPACE_UNAVAILABLE":
      return "Your workspace could not be reached right now. Nothing was changed. Try again shortly.";
    case "WORKSPACE_VIEW_TOO_LARGE":
      return "Your workspace is too large to read here. Nothing was changed.";
    case "OVERLAY_VERSION_UNKNOWN":
      return "Your workspace changes need to sync again. Sync, then send again.";
    case "SESSION_FORBIDDEN":
      return "You do not have access to this conversation.";
    case "TURN_NOT_FOUND":
      return "This message could not be found.";
    case "WORK_ITEM_NOT_FOUND":
      return "This item is no longer part of the task.";
    case "PACKAGE_SPEND_REFUSED":
      return "The org check was not repeated because nothing changed since the last one. Ask for changes to try something different.";
    case "PACKAGE_SUBMIT_FAILED":
      return "The check could not be submitted to your org. Nothing was changed in the org. Check the org connection, then run the check again.";
    case "GRANT_CHECK_UNAVAILABLE":
      return "Permissions could not be checked right now. Nothing was changed. Try again shortly.";
    case "GRANT_REQUIRED":
    case "GRANT_ABSENT":
    case "GRANT_REVOKED":
    case "GRANT_NOT_FOUND":
    case "GRANT_HASH_MISMATCH":
    case "GRANT_SCOPE_NOT_COVERED":
    case "GRANT_SCOPE_MISMATCH":
    case "GRANT_OP_CLASS_MISMATCH":
    case "GRANT_TASK_MISMATCH":
    case "GRANT_SUBJECT_MISMATCH":
      return "The approval this step relied on no longer covers it. Review the task's current state and approve again if needed.";
    case "NOTHING_TO_COMPACT":
      return "There is nothing new to compact yet.";
    case "COMPACT_UNAVAILABLE":
      return "The summary could not be written. Nothing was changed. You can try again.";
    case "ATTACHMENT_TOO_LARGE":
      return "Those attachments are too large or too many for one message. Remove some and try again.";
    case "NO_PACKAGE":
      return "There is nothing assembled yet for this task to save.";
    case "UNKNOWN_MEMBER":
      return "One of the selected files is not part of this package. Refresh and try again.";
    case "NOTHING_INCLUDED":
      return "Nothing was left to save after the selection. Check at least one file.";
    case "BRANCH_SAVE_UNAVAILABLE":
      return "Could not reach your workspace branch right now. Nothing was saved. Try again.";
    case "BRANCH_SAVE_REPLAYED":
      return "The branch reported this save as already applied in an unexpected way. Try saving again.";
    case "BRANCH_CONFLICT":
      return "The branch moved since these files were read. Nothing was saved. Try again.";
    case "TURN_INVALID":
      return "That message could not be accepted (it may be too long or have too many attachments). For very long text, attach it as a file instead.";
    case "BRANCH_SAVE_REJECTED":
      return "Your workspace branch rejected this save.";
    case "BRANCH_SAVE_IN_PROGRESS":
      return "A save of this change is already running.";
    case "BRANCH_SAVE_BUSY":
      return "Saves are busy right now. Nothing was saved. Try again in a moment.";
    case "BRANCH_SAVE_PACKAGE_MOVED":
      return "The change was updated while saving. Save again.";
    default:
      break;
  }
  if (err.status === 429) {
    // A rate limit with no typed code: transient by nature, but not the budget (that is `LLM_BUDGET_EXCEEDED`).
    return "Too many requests right now. Wait a moment, then try again.";
  }
  if (err.status === 422) {
    // A malformed request is a client bug, not a user-facing state.
    return "The app sent a request the server could not accept. This is a bug. Please report it.";
  }
  return `The request failed (${err.status}). Nothing was changed. You can try again.`;
}

/**
 * Refusals that cannot succeed by repeating the same request: the copy for these never says "try again".
 * (A transient code — CAPACITY, WORKSPACE_UNAVAILABLE, PACKAGE_SUBMIT_FAILED, GRANT_CHECK_UNAVAILABLE, LANE_V2_OFFLINE — is
 * not listed.) The tests pin both halves: no permanent code's copy contains "try again", and this set is the source of truth.
 */
export const PERMANENT_LANE_V2_CODES: ReadonlySet<string> = new Set([
  "LLM_BUDGET_EXCEEDED", "HOLD_REQUEST_INVALID", "HUMAN_CHANGE_CAP_REACHED", "SAVE_TARGET_IS_LOCAL",
  "ILLEGAL_TRANSITION", "TRANSITION_REFUSED", "VALIDATION_NOT_PASSED", "ASK_INVALID", "WORKSPACE_SCOPE_MISMATCH",
  "WORKSPACE_NOT_FOUND", "WORKSPACE_INVALID", "WORKSPACE_VIEW_TOO_LARGE", "SESSION_FORBIDDEN", "TURN_NOT_FOUND",
  "WORK_ITEM_NOT_FOUND", "PACKAGE_SPEND_REFUSED", "GRANT_REQUIRED", "GRANT_ABSENT", "GRANT_REVOKED", "GRANT_NOT_FOUND",
  "GRANT_HASH_MISMATCH", "GRANT_SCOPE_NOT_COVERED", "GRANT_SCOPE_MISMATCH", "GRANT_OP_CLASS_MISMATCH", "GRANT_TASK_MISMATCH",
  "GRANT_SUBJECT_MISMATCH", "BRANCH_SAVE_IN_PROGRESS", "BRANCH_SAVE_PACKAGE_MOVED",
]);

// ── transport ────────────────────────────────────────────────────────────────────────────────────

async function readBody(res: Response): Promise<unknown> {
  const text = await res.text().catch(() => "");
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function header(res: Response, name: string): string | null {
  try {
    return res.headers?.get(name) ?? null;
  } catch {
    return null;
  }
}

export interface RequestOptions {
  signal?: AbortSignal;
}

async function request<T>(method: "GET" | "POST", path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  return (await requestWithStatus<T>(method, path, body, opts)).body;
}

/** Same as `request`, but also exposes the HTTP status — for the one call whose 200 and 202 mean different things. */
async function requestWithStatus<T>(
  method: "GET" | "POST",
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<{ status: number; body: T }> {
  const res = await fetch(`${base()}${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      Accept: "application/json",
      ...getMultiAgentAuthHeaders(),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  const parsed = await readBody(res);
  if (!res.ok) {
    const code = errorCodeOf(parsed);
    const err = new LaneV2Error(
      res.status,
      code,
      `${method} ${path.split("?")[0]} → ${res.status}${code ? ` ${code}` : ""}`,
      parsed,
      parseRetryAfter(header(res, "Retry-After")),
    );
    throw err;
  }
  return { status: res.status, body: parsed as T };
}

const enc = encodeURIComponent;
const S = (sessionId: string) => `/api/sessions/${enc(sessionId)}`;

// ── R0–R5 (read model) ───────────────────────────────────────────────────────────────────────────

/** R0. A 404 (old server) means "no read model: stay legacy" — decided by `lane.ts`, not here. */
export function getCapabilities(opts?: RequestOptions): Promise<LaneV2Capabilities> {
  return request("GET", "/api/sessions/capabilities", undefined, opts);
}

/** R1. 404 for anything that is not the caller's v2 session. Never pins a session. */
export function getSession(sessionId: string, opts?: RequestOptions): Promise<LaneV2Session> {
  return request("GET", S(sessionId), undefined, opts);
}

/** R2. Oldest first. */
export async function getAsks(
  sessionId: string,
  status: "pending" | "all" = "pending",
  opts?: RequestOptions,
): Promise<AskView[]> {
  const out = await request<{ asks?: AskView[] }>("GET", `${S(sessionId)}/asks?status=${status}`, undefined, opts);
  return Array.isArray(out?.asks) ? out.asks : [];
}

/** R3. */
export function getTask(sessionId: string, taskId: string, opts?: RequestOptions): Promise<TaskSnapshot> {
  return request("GET", `${S(sessionId)}/tasks/${enc(taskId)}`, undefined, opts);
}

/**
 * R7. The task's change graph (`version` = a plan version, default the latest; `since` = a version
 * strictly below the served one, adds the diff). A `since` at or above the served version, or an
 * unknown version, is 404 `PLAN_VERSION_NOT_FOUND`; a task without a plan is the ordinary task 404.
 */
export function getTaskGraph(
  sessionId: string,
  taskId: string,
  opts: { version?: number; since?: number } & RequestOptions = {},
): Promise<TaskGraph> {
  const params = new URLSearchParams();
  if (typeof opts.version === "number") params.set("version", String(opts.version));
  if (typeof opts.since === "number") params.set("since", String(opts.since));
  const qs = params.toString();
  return request("GET", `${S(sessionId)}/tasks/${enc(taskId)}/graph${qs ? `?${qs}` : ""}`, undefined, opts);
}

/** R4. */
export function getItemBody(sessionId: string, taskId: string, workItemId: string, opts?: RequestOptions): Promise<ItemBody> {
  return request("GET", `${S(sessionId)}/tasks/${enc(taskId)}/items/${enc(workItemId)}/body`, undefined, opts);
}

/** R5. Oldest first. */
export async function getTurns(sessionId: string, opts?: RequestOptions): Promise<TurnView[]> {
  const out = await request<{ turns?: TurnView[] }>("GET", `${S(sessionId)}/turns`, undefined, opts);
  return Array.isArray(out?.turns) ? out.turns : [];
}

/** R6. The caller's own v2 sessions, newest activity first. Only called when R0 `session_list` is true. */
export function getSessions(
  opts: { limit?: number; includeClosed?: boolean } & RequestOptions = {},
): Promise<SessionListOut> {
  const params = new URLSearchParams();
  if (typeof opts.limit === "number") params.set("limit", String(opts.limit));
  if (opts.includeClosed) params.set("include_closed", "true");
  const qs = params.toString();
  return request("GET", `/api/sessions${qs ? `?${qs}` : ""}`, undefined, opts);
}

// ── write routes (on dev today) ──────────────────────────────────────────────────────────────────

/**
 * Mint a stream ticket. PINS the session to lane v2 — call only for a session that resolved v2 (or a
 * brand-new client-owned id the lane resolution said may be v2). Never for a legacy session: after a
 * mint the old routes 409 it forever.
 */
export function mintTicket(sessionId: string, opts?: RequestOptions): Promise<TicketOut> {
  return request("POST", "/api/sessions/ticket", { session_id: sessionId }, opts);
}

/** The outcome of `POST /turns` — the response is a bonus; `detached` is not a failure. */
export type TurnSubmitResult =
  | { kind: "done"; out: TurnOut }
  | { kind: "detached"; reason: "gateway_timeout" | "network" | "aborted" };

/** Statuses a proxy answers while the turn itself keeps running upstream. */
const DETACHED_STATUSES = new Set([502, 504]);

export interface SubmitTurnOptions extends RequestOptions {
  /** Called before each CAPACITY wait, with the attempt number and the wait. */
  onBusy?: (info: { attempt: number; waitMs: number }) => void;
  /** Max CAPACITY retries (default 4). */
  maxCapacityRetries?: number;
  /** Injected for tests. */
  sleep?: (ms: number) => Promise<void>;
  /**
   * The answered pre-task INFORM ask this turn continues deterministically (server's `bind_reply`
   * LLM is skipped). Only ever sent when the caller actually has one — a server without the
   * `turn_answers_ask` capability REFUSES an unknown field (422, `extra=forbid`).
   */
  answersAskId?: string;
  /**
   * Pre-resolved attachments (leg `v2-composer-parity`). Only ever sent when the caller has any — a
   * server without `turn_attachments` refuses the unknown field, same rule as `answersAskId`.
   */
  attachments?: AttachmentIn[];
  /**
   * The IDE snapshot this turn is submitted with (leg `v2-composer-parity`). Only ever sent when the
   * caller has one — a server without `turn_ide_state` refuses the unknown field.
   */
  ideState?: IdeStateIn | null;
}

/** Bounded wait for one CAPACITY retry: server's Retry-After, else 5 s; clamped to [1 s, 30 s]. */
export function capacityWaitMs(retryAfterMs: number | null): number {
  const ms = retryAfterMs ?? 5000;
  return Math.min(30_000, Math.max(1000, ms));
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * `POST /api/sessions/turns`. Retries 503 CAPACITY (bounded, `Retry-After`); maps a gateway timeout
 * or a dropped connection to `{kind:"detached"}`; throws `LaneV2Error` for every other refusal
 * (409 TURN_BUSY, 409 WRONG_LANE, 422 …) — those mean the turn did NOT start.
 */
export async function submitTurn(
  sessionId: string,
  text: string,
  opts: SubmitTurnOptions = {},
): Promise<TurnSubmitResult> {
  const sleep = opts.sleep ?? defaultSleep;
  const maxRetries = opts.maxCapacityRetries ?? 4;
  for (let attempt = 0; ; attempt += 1) {
    try {
      const out = await request<TurnOut>(
        "POST",
        "/api/sessions/turns",
        {
          session_id: sessionId,
          text,
          // Only when the caller has some: the field itself is refused (422) by a server that
          // predates `turn_attachments`, and `sendTurn` only ever passes it under that capability.
          ...(opts.attachments?.length ? { attachments: opts.attachments } : {}),
          ...(opts.answersAskId ? { answers_ask_id: opts.answersAskId } : {}),
          ...(opts.ideState ? { ide_state: opts.ideState } : {}),
        },
        opts,
      );
      return { kind: "done", out };
    } catch (err) {
      if (err instanceof LaneV2Error) {
        if (err.status === 503 && err.code === "CAPACITY" && attempt < maxRetries) {
          const waitMs = capacityWaitMs(err.retryAfterMs);
          opts.onBusy?.({ attempt: attempt + 1, waitMs });
          await sleep(waitMs);
          continue;
        }
        if (DETACHED_STATUSES.has(err.status)) return { kind: "detached", reason: "gateway_timeout" };
        throw err;
      }
      if ((err as { name?: string })?.name === "AbortError") return { kind: "detached", reason: "aborted" };
      // A fetch that failed mid-flight (proxy reset, laptop sleep). The turn may well be running.
      return { kind: "detached", reason: "network" };
    }
  }
}

/**
 * The body a lane-v2 ask accepts. MSA REFUSES `input`/`answers`/`edits`/`override_reason`/`selected`
 * on this route, so the type has no room for them; `subject` is always the interaction's own, echoed
 * unchanged.
 */
export interface AskRespondBody {
  option_id?: string;
  free_text?: string;
  ref_id?: string;
  subject?: InteractionSubject | null;
}

/** `POST /api/sessions/{s}/asks/{a}/respond` → the `interaction.resolved` payload. */
export function respondToAsk(sessionId: string, askId: string, body: AskRespondBody): Promise<AskResolution> {
  return request("POST", `${S(sessionId)}/asks/${enc(askId)}/respond`, body);
}

/** `POST /api/sessions/{s}/turns/{turn}/cancel`. 202 (requested) or 200 (already terminal). */
export function cancelTurn(sessionId: string, turnId: string): Promise<Record<string, unknown>> {
  return request("POST", `${S(sessionId)}/turns/${enc(turnId)}/cancel`, {});
}

/** What the run inbox answers when it accepts a message: its ordinal in that turn's inbox, and how many are queued. */
export interface TurnMessageOut {
  ok: boolean;
  message_id: string;
  inbox_seq: number;
  pending: number;
}

/** How long to wait before the one retry of a busy inbox when the server named no `Retry-After` (it sends 1 s). */
const INBOX_BUSY_DEFAULT_WAIT_MS = 1000;

/**
 * The Lane V2 steer route, `POST /api/sessions/{s}/turns/{t}/messages` (MSA leg `ux-steer`, D10): a message for a turn that
 * is RUNNING. 200 `{ok, message_id, inbox_seq, pending}`. MSA reads the inbox between the turn's steps; the turn's own
 * result says what became of the message (`TurnOut.detail.follow_ups`). Refusals, by `error_code` only:
 * 404 `SESSION_NOT_FOUND` / `TURN_NOT_FOUND`, 409 `TURN_ENDED` (`followUps.isRunTerminal`: the caller sends the text as a
 * normal turn), 503 `LANE_V2_OFFLINE`, 422 for a bad body. 503 `INBOX_BUSY` is retried ONCE after the server's `Retry-After`.
 */
export async function postTurnMessage(
  sessionId: string,
  turnId: string,
  text: string,
  opts: { sleep?: (ms: number) => Promise<void> } = {},
): Promise<TurnMessageOut> {
  const sleep = opts.sleep ?? defaultSleep;
  const path = `${S(sessionId)}/turns/${enc(turnId)}/messages`;
  try {
    return await request<TurnMessageOut>("POST", path, { text });
  } catch (err) {
    if (!isLaneV2Error(err) || err.code !== "INBOX_BUSY") throw err;
    await sleep(err.retryAfterMs ?? INBOX_BUSY_DEFAULT_WAIT_MS);
    return await request<TurnMessageOut>("POST", path, { text });
  }
}

/** `POST /api/sessions/{s}/tasks/{t}/cancel`. `cancelled:false` carries the refusal `reason`. */
export function cancelTask(sessionId: string, taskId: string): Promise<TaskCancelOut> {
  return request("POST", `${S(sessionId)}/tasks/${enc(taskId)}/cancel`, {});
}

/** `POST /api/sessions/{s}/tasks/{t}/hold`: pause after the component in flight (always 200; the typed `status` says what happened). */
export function holdTask(sessionId: string, taskId: string): Promise<TaskHoldOut> {
  return request("POST", `${S(sessionId)}/tasks/${enc(taskId)}/hold`, { reason: "user" });
}

/** `POST /api/sessions/{s}/tasks/{t}/resume`: pick a held task up again. A budget hold refuses `409 HOLD_BUDGET_STILL_EXHAUSTED` until a re-check passes. */
export function resumeTask(sessionId: string, taskId: string): Promise<TaskHoldOut> {
  return request("POST", `${S(sessionId)}/tasks/${enc(taskId)}/resume`, {});
}

/** `POST /api/sessions/{s}/tasks/{t}/revert` → opens a NEW task with a `confirm_revert` ask. */
export function revertTask(sessionId: string, taskId: string): Promise<RevertOut> {
  return request("POST", `${S(sessionId)}/tasks/${enc(taskId)}/revert`, { pre_apply_bodies: {} });
}

/**
 * `POST /api/sessions/{id}/close`. Only ever called for a v2 session the caller owns — `409
 * SESSION_BUSY` for an open task or a live turn, idempotent on an already-closed session.
 */
export function closeSession(sessionId: string): Promise<SessionCloseOut> {
  return request("POST", `${S(sessionId)}/close`, {});
}

/**
 * `GET /api/sessions/{id}/compaction` (MSA leg `cv-compact`): the session's live compaction, or
 * `null`. Also the feature probe: a server without the compact routes answers 404.
 */
export function getCompaction(sessionId: string, opts?: RequestOptions): Promise<SessionCompactionOut> {
  return request("GET", `${S(sessionId)}/compaction`, undefined, opts);
}

/**
 * `POST /api/sessions/{id}/compact`: fold the conversation so far into one stored summary (one model
 * call on the server). `409 NOTHING_TO_COMPACT` when nothing was said since the last one, `409
 * SESSION_BUSY` while a turn runs, `502 COMPACT_UNAVAILABLE` when no complete summary came back —
 * nothing is stored in any of those. `focus` is what the reader asked the summary to keep.
 */
export function compactSession(sessionId: string, focus?: string | null): Promise<SessionCompactOut> {
  const kept = (focus ?? "").trim();
  return request("POST", `${S(sessionId)}/compact`, kept ? { focus: kept } : {});
}

/**
 * `POST /api/sessions/{s}/tasks/{t}/save` (leg `v2-save-branch`). Saves the task's package to the
 * caller's own workspace branch — never a git write and never an org write (`check_only`/`apply` are
 * those). `409 BRANCH_CONFLICT` / `422 BRANCH_SAVE_REJECTED` carry extra structured detail beyond the
 * bare `{error_code}` — read it off the thrown `LaneV2Error` with `branchConflictOf`/`branchSaveRejectionOf`.
 */
export type SaveBranchPost =
  | { kind: "done"; out: SaveBranchOut }
  | { kind: "accepted"; accepted: BranchSaveAccepted };

/** `200` → the save body; `202` → a background save to poll. Discriminated on the HTTP status, never on the body's shape. */
export async function saveBranch(
  sessionId: string,
  taskId: string,
  body: SaveBranchIn,
  opts?: RequestOptions,
): Promise<SaveBranchPost> {
  const res = await requestWithStatus<SaveBranchOut | BranchSaveAccepted>(
    "POST",
    `${S(sessionId)}/tasks/${enc(taskId)}/save`,
    body,
    opts,
  );
  return res.status === 202
    ? { kind: "accepted", accepted: res.body as BranchSaveAccepted }
    : { kind: "done", out: res.body as SaveBranchOut };
}

/** `GET /api/sessions/{s}/tasks/{t}/saves/{save_id}` (contracts 0.2.35): the background save's progress / outcome. 404 = gone. */
export function getBranchSave(sessionId: string, taskId: string, saveId: string, opts?: RequestOptions): Promise<BranchSavePoll> {
  return request("GET", `${S(sessionId)}/tasks/${enc(taskId)}/saves/${enc(saveId)}`, undefined, opts);
}

/**
 * `POST /api/sessions/{s}/tasks/{t}/validate` (contracts 0.2.30): re-run the check-only deployment against the live org for a
 * task at rest in `validated`. 202 `{status:"recorded"|"replayed"}`; the same `idempotency_key` replays (one check), so a click
 * mints a FRESH key (`newValidateKey`) and a retry of that same click reuses it. 409 `NOT_VALIDATABLE` /
 * `VALIDATION_IN_PROGRESS` / `REVALIDATE_LIMIT_REACHED` / `DEPLOY_NOT_ENABLED`, 503 `LANE_V2_OFFLINE`.
 */
export function validateTask(sessionId: string, taskId: string, idempotencyKey: string): Promise<ValidateOut> {
  return request("POST", `${S(sessionId)}/tasks/${enc(taskId)}/validate`, { idempotency_key: idempotencyKey });
}

/**
 * `POST /api/sessions/{s}/tasks/{t}/request-changes` (RMV 14): send a RESTING built changeset back with the person's text. 202
 * `{status:"recorded"|"replayed"}`. 422 `FREE_TEXT_REQUIRED`, 409 `NOT_CHANGEABLE` / `HUMAN_CHANGE_CAP_REACHED`, 503 `LANE_V2_OFFLINE`.
 */
export function requestChanges(sessionId: string, taskId: string, text: string, idempotencyKey: string): Promise<RequestChangesOut> {
  return request("POST", `${S(sessionId)}/tasks/${enc(taskId)}/request-changes`, { text, idempotency_key: idempotencyKey });
}

/**
 * `POST /api/sessions/{s}/tasks/{t}/discard` (RMV 14): discard a RESTING built changeset; the task ends `declined`. 200
 * `{status, ended_cause}`. 409 `NOT_DISCARDABLE`. Works while Lane V2 is offline (it is a decline).
 */
export function discardTask(sessionId: string, taskId: string, idempotencyKey: string): Promise<DiscardOut> {
  return request("POST", `${S(sessionId)}/tasks/${enc(taskId)}/discard`, { idempotency_key: idempotencyKey });
}

/** A fresh idempotency key (1..128 chars) for one Request changes / Discard click. */
export function newActionKey(prefix: "rc" | "dc"): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return `${prefix}-${c.randomUUID()}`;
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** A fresh idempotency key (1..128 chars) for one Validate click. */
export function newValidateKey(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return `v-${c.randomUUID()}`;
  return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** The session stream URL (the ticket is a bearer in a URL — callers never log it). */
export function sessionEventsUrl(sessionId: string, ticket: string): string {
  return `${base()}${S(sessionId)}/events?ticket=${enc(ticket)}`;
}
